import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Syringe, 
  AlertTriangle, 
  AlertCircle,
  CheckCircle, 
  Calendar,
  ChevronUp,
  ChevronDown,
  User,
  PawPrint,
  MessageSquare
} from 'lucide-react';

import VaccinationForm from '../components/Forms/VaccinationForm';
import toast from 'react-hot-toast';
import { getVaccinations, addVaccination, updateVaccination, deleteVaccination } from '../services/vaccinationService';
import { getPets } from '../services/petService';
import { getOwners } from '../services/ownerService';
import { Vaccination, Pet, Owner } from '../types';
import { SkeletonListPage } from '../components/Skeleton/Skeleton';
import EmptyState from '../components/EmptyState/EmptyState';
import { getVaccinationStatus as getStatusUtil, getDaysLeft, VaccinationStatus } from '../utils/vaccinationStatus';
import DeleteConfirmModal from '../components/Modals/DeleteConfirmModal';

const Vaccinations: React.FC = () => {
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [vaccinationToDelete, setVaccinationToDelete] = useState<Vaccination | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVaccination, setSelectedVaccination] = useState<Vaccination | null>(null);
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'overdue' | 'approaching' | 'completed'>('all');

  const [sortField, setSortField] = useState<'date' | 'petName' | 'ownerName' | 'name' | 'status'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');



  const fetchVaccinations = () => {
    setLoading(true);
    getVaccinations()
      .then(data => setVaccinations(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchVaccinations();
    getPets().then(setPets).catch(err => toast.error('Hasta listesi yüklenemedi: ' + (err?.message || 'bilinmeyen hata')));
    getOwners().then(setOwners).catch(err => toast.error('Sahip listesi yüklenemedi: ' + (err?.message || 'bilinmeyen hata')));
  }, []);

  const getVaccinationWithPet = (vaccination: Vaccination) => {
    const pet = pets.find(p => p.id === vaccination.pet_id || p.id === vaccination.petId);
    const owner = pet ? owners.find(o => o.id === pet.owner_id) : null;
    return { ...vaccination, pet, owner };
  };

  const handleSort = (field: 'date' | 'petName' | 'ownerName' | 'name' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Kartlara tıklama fonksiyonları
  const handleCardClick = (filter: 'all' | 'overdue' | 'approaching' | 'completed') => {
    setStatusFilter(filter);
    setSearchTerm(''); // Arama terimini temizle
  };

  const getSortIcon = (field: 'date' | 'petName' | 'ownerName' | 'name' | 'status') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  // TEK VE DOĞRU DURUM FONKSİYONU: src/utils/vaccinationStatus.ts
  // Sonraki doz tarihi (next_due_date) esas alınır; yerel takvim günüyle karşılaştırılır.
  const getVaccinationStatus = (vaccination: Vaccination, all: Vaccination[] = vaccinations): VaccinationStatus =>
    getStatusUtil(vaccination, all);

  // Tek fonksiyondan türetilen görsel bilgi
  const getVaccinationStatusInfo = (vaccination: Vaccination) => {
    const status = getVaccinationStatus(vaccination, vaccinations);
    const daysLeft = getDaysLeft(vaccination);

    switch (status) {
      case 'completed':
        return { status: 'Tamamlandı', statusClass: 'bg-green-100 text-green-800', icon: '✓' };
      case 'overdue':
        return { status: `${Math.abs(daysLeft)} gün gecikmiş`, statusClass: 'bg-red-100 text-red-800', icon: '⚠' };
      case 'approaching':
        return { status: daysLeft === 0 ? 'Bugün!' : `${daysLeft} gün kaldı`, statusClass: 'bg-amber-100 text-amber-800', icon: '⏰' };
      default:
        return { status: 'Planlandı', statusClass: 'bg-blue-100 text-blue-800', icon: '📅' };
    }
  };

  // Önce tüm aşıları pet ve owner bilgileri ile birleştir
  const allVaccinationsWithPets = vaccinations.map(getVaccinationWithPet);
  
  // Kartlardaki hesaplama mantığını kullanarak filtreleme
  const filteredVaccinations = allVaccinationsWithPets.filter(vaccination => {
    // Arama filtreleme
    if (searchTerm.trim()) {
      const petName = vaccination.pet?.name?.toLowerCase() || '';
      const vaccName = (vaccination.name || '').toLowerCase();
      const ownerName = vaccination.pet?.owner?.name?.toLowerCase() || '';
      const searchTermLower = searchTerm.toLowerCase().trim();
      const matchesSearch = (
        petName.includes(searchTermLower) ||
        vaccName.includes(searchTermLower) ||
        ownerName.includes(searchTermLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Status filtreleme
    if (statusFilter === 'all') return true;

    return getVaccinationStatus(vaccination, vaccinations) === statusFilter;
  })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'petName':
          aValue = a.pet?.name || '';
          bValue = b.pet?.name || '';
          break;
        case 'ownerName':
          aValue = a.pet?.owner?.name || '';
          bValue = b.pet?.owner?.name || '';
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSubmitForm = async (data: any) => {
    try {
      if (selectedVaccination) {
        // Güncelleme işlemi
        await updateVaccination(selectedVaccination.id, data);
        toast.success('Aşı başarıyla güncellendi!');
      } else {
        // Yeni ekleme işlemi
        await addVaccination(data);
        toast.success('Aşı başarıyla eklendi!');
      }
      setShowForm(false);
      setSelectedVaccination(null);
      fetchVaccinations();
    } catch (err: any) {
      toast.error('Hata: ' + err.message);
    }
  };



  const handleDeleteVaccination = async (vaccinationId: string) => {
    try {
      await deleteVaccination(vaccinationId);
      fetchVaccinations();
      toast.success('Aşı kaydı silindi!');
    } catch (error: any) {
      toast.error('Silme hatası: ' + (error?.message || 'Bilinmeyen hata'));
    }
  };

  const handleStatusChange = async (vaccinationId: string, newStatus: string) => {
    try {
      // Veritabanını güncelle
      await updateVaccination(vaccinationId, { status: newStatus as any });
      
      // Local state'i güncelle
      setVaccinations(prev => prev.map(vacc => 
        vacc.id === vaccinationId ? { ...vacc, status: newStatus as any } : vacc
      ));
      
      toast.success('Aşı durumu güncellendi!');
    } catch (error: any) {
      toast.error('Hata: ' + error.message);
    }
  };

  const handleViewPet = (pet: Pet) => {
    setSelectedPet(pet);
    setSelectedVaccination(null);
    setShowModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };



  const getStatusColor = (vaccination: Vaccination) => {
    const status = getVaccinationStatus(vaccination, vaccinations);
    switch (status) {
      case 'completed':  return 'bg-green-100 text-green-800';
      case 'overdue':    return 'bg-red-100 text-red-800';
      case 'approaching': return 'bg-amber-100 text-amber-800';
      default:           return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (vaccination: Vaccination) => {
    return getVaccinationStatusInfo(vaccination).status;
  };

  // Kart sayilari, filtreyle ayni durum kurallarini kullanir
  const overdueVaccinations = allVaccinationsWithPets.filter(vacc => getVaccinationStatus(vacc, vaccinations) === 'overdue');
  const upcomingVaccinations = allVaccinationsWithPets.filter(vacc => getVaccinationStatus(vacc, vaccinations) === 'approaching');
  const completedVaccinations = allVaccinationsWithPets.filter(vacc => getVaccinationStatus(vacc, vaccinations) === 'completed');

  if (loading) return <SkeletonListPage />;
  if (error) return <div className="text-center py-12 text-red-600">{error}</div>;

  return (
    <div className="page-stack">
      {/* Page Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">Aşılar</h1>
          <p className="page-subtitle">Tüm aşı kayıtları</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Aşı</span>
        </button>
      </div>

      {/* Filters */}
      <div className="dribbble-panel p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
          <input
            type="text"
            placeholder="Aşı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Durum Özeti Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Gecikmiş */}
        <button type="button" onClick={() => handleCardClick('overdue')}
          className={`dribbble-panel p-4 text-left transition-all ${statusFilter === 'overdue' ? 'ring-2 ring-red-400/70 bg-red-50' : 'bg-white hover:bg-red-50/50'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Gecikmiş</p>
              <p className="mt-2 text-3xl font-bold text-red-600">{overdueVaccinations.length}</p>
              <p className="mt-1 text-xs text-[#8A8676]">Süresi geçen aşılar</p>
            </div>
            <span className="rounded-xl bg-red-100 p-2"><AlertTriangle className="w-5 h-5 text-red-600" /></span>
          </div>
        </button>

        {/* 30 gün içinde */}
        <button type="button" onClick={() => handleCardClick('approaching')}
          className={`dribbble-panel p-4 text-left transition-all ${statusFilter === 'approaching' ? 'ring-2 ring-amber-400/70 bg-amber-50' : 'bg-white hover:bg-amber-50/50'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Yaklaşan</p>
              <p className="mt-2 text-3xl font-bold text-amber-600">{upcomingVaccinations.length}</p>
              <p className="mt-1 text-xs text-[#8A8676]">30 gün içinde</p>
            </div>
            <span className="rounded-xl bg-amber-100 p-2"><Calendar className="w-5 h-5 text-amber-600" /></span>
          </div>
        </button>

        {/* Tamamlanan */}
        <button type="button" onClick={() => handleCardClick('completed')}
          className={`dribbble-panel p-4 text-left transition-all ${statusFilter === 'completed' ? 'ring-2 ring-emerald-400/70 bg-emerald-50' : 'bg-white hover:bg-emerald-50/50'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Tamamlanan</p>
              <p className="mt-2 text-3xl font-bold text-emerald-600">{completedVaccinations.length}</p>
              <p className="mt-1 text-xs text-[#8A8676]">Yapılan aşılar</p>
            </div>
            <span className="rounded-xl bg-emerald-100 p-2"><CheckCircle className="w-5 h-5 text-emerald-600" /></span>
          </div>
        </button>

        {/* Toplam */}
        <button type="button" onClick={() => handleCardClick('all')}
          className={`dribbble-panel p-4 text-left transition-all ${statusFilter === 'all' ? 'ring-2 ring-[#557236]/70 bg-[#E8EFD9]' : 'bg-white hover:bg-[#E8EFD9]/50'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#557236]">Toplam</p>
              <p className="mt-2 text-3xl font-bold text-[#557236]">{allVaccinationsWithPets.length}</p>
              <p className="mt-1 text-xs text-[#8A8676]">Tüm kayıtlar</p>
            </div>
            <span className="rounded-xl bg-[#E8EFD9] p-2"><Syringe className="w-5 h-5 text-[#557236]" /></span>
          </div>
        </button>
      </div>

      {/* Liste — Kart bazlı modern tasarım */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="section-title mb-0">
            {statusFilter === 'all' ? 'Tüm Aşılar' :
             statusFilter === 'overdue' ? 'Gecikmiş Aşılar' :
             statusFilter === 'approaching' ? 'Yaklaşan Aşılar' :
             statusFilter === 'completed' ? 'Tamamlanan Aşılar' : 'Tüm Aşılar'}
            <span className="ml-2 text-xs font-normal text-[#8A8676]">({filteredVaccinations.length})</span>
          </h3>
          {statusFilter !== 'all' && (
            <button
              onClick={() => handleCardClick('all')}
              className="text-xs text-[#557236] hover:text-[#3F5728] font-semibold"
            >
              Tüm aşıları göster →
            </button>
          )}
        </div>

        {filteredVaccinations.length === 0 ? (
          <div className="dribbble-panel">
            <EmptyState
              icon={Syringe}
              iconColor="purple"
              title="Aşı kaydı bulunamadı"
              description="Hayvanların aşı geçmişini kayıt altına alın."
              actionLabel="Yeni Aşı Kaydı"
              onAction={() => setShowForm(true)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredVaccinations.map((vaccination, index) => {
              const statusInfo = getVaccinationStatusInfo(vaccination);
              const nextDate = vaccination.next_due_date || vaccination.nextDueDate;
              const noteText = vaccination.notes || '';
              const nextMatch = noteText.match(/Sonraki:\s*([^|\n]+)/i);
              const nextVaccineName = nextMatch ? nextMatch[1].trim() : null;
              const cleanNotes = noteText.replace(/\s*\|?\s*Sonraki:\s*[^|\n]+/i, '').trim();

              return (
                <motion.div
                  key={vaccination.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="dribbble-panel p-4 group relative"
                >
                  {/* Üst — Aşı ikonu + ad + tarih */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center shrink-0">
                      <Syringe className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#2B2B2B] truncate">{vaccination.name}</p>
                      <p className="text-xs text-[#8A8676]">{formatDate(vaccination.date)}</p>
                    </div>
                    {/* Status badge sağ üstte */}
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.statusClass}`}>
                      {statusInfo.status}
                    </span>
                  </div>

                  {/* Hasta + Sahip rozetleri */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <button
                      onClick={() => vaccination.pet && handleViewPet(vaccination.pet)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B] hover:bg-[#E8EFD9] hover:text-[#3F5728] transition-colors"
                    >
                      <PawPrint className="w-3 h-3" />
                      {vaccination.pet?.name}
                    </button>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      <User className="w-3 h-3" />
                      {vaccination.pet?.owner?.name}
                    </span>
                  </div>

                  {/* Hatırlatma kutusu — sonraki aşı */}
                  {nextDate && (
                    <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 leading-none mb-0.5">
                            Sonraki Doz · {nextVaccineName || vaccination.name}
                          </p>
                          <p className="text-[11px] text-amber-800 font-semibold">{formatDate(nextDate)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Not */}
                  {cleanNotes && (
                    <div className="flex items-start gap-1.5 mb-3" title={cleanNotes}>
                      <MessageSquare className="w-3 h-3 text-[#B0A99A] shrink-0 mt-0.5" />
                      <p className="text-[11px] text-[#8A8676] line-clamp-2 italic flex-1">
                        {cleanNotes}
                      </p>
                    </div>
                  )}

                  {/* Aksiyon — hover ile sağ üstte */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-lg p-1 shadow-sm border border-[#F0EBE1]">
                    {vaccination.status !== 'completed' && (
                      <button
                        onClick={() => handleStatusChange(vaccination.id, 'completed')}
                        className="w-6 h-6 rounded-md hover:bg-emerald-50 text-emerald-600 flex items-center justify-center"
                        title="Tamamlandı işaretle"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => { setSelectedVaccination(vaccination); setShowForm(true); }}
                      className="w-6 h-6 rounded-md hover:bg-blue-50 text-blue-600 flex items-center justify-center"
                      title="Düzenle"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setVaccinationToDelete(vaccination)}
                      className="w-6 h-6 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center"
                      title="Sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>



      {/* Vaccination Form */}
      <VaccinationForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedVaccination(null);
        }}
        onSubmit={handleSubmitForm}
        vaccination={selectedVaccination}
      />

      {/* Pet Detail Modal */}
      {showModal && selectedPet && createPortal(
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[9999]">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="dribbble-panel p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#2B2B2B]">Hasta Detayları</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedPet(null);
                }}
                className="text-[#B0A99A] hover:text-[#8A8676]"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="text-3xl">
                  {selectedPet.species === 'dog' ? '🐕' : 
                   selectedPet.species === 'cat' ? '🐱' : 
                   selectedPet.species === 'bird' ? '🐦' : 
                   selectedPet.species === 'rabbit' ? '🐰' : '🐾'}
                </div>
                <div>
                  <h3 className="font-medium text-[#2B2B2B]">{selectedPet.name}</h3>
                  <p className="text-sm text-[#8A8676]">{selectedPet.breed}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#8A8676]">Tür</p>
                  <p className="font-medium">
                    {selectedPet.species === 'dog' ? 'Köpek' : 
                     selectedPet.species === 'cat' ? 'Kedi' : 
                     selectedPet.species === 'bird' ? 'Kuş' : 
                     selectedPet.species === 'rabbit' ? 'Tavşan' : 'Diğer'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#8A8676]">Yaş</p>
                  <p className="font-medium">{selectedPet.age} yaş</p>
                </div>
                <div>
                  <p className="text-sm text-[#8A8676]">Kilo</p>
                  <p className="font-medium">{selectedPet.weight} kg</p>
                </div>
                <div>
                  <p className="text-sm text-[#8A8676]">Renk</p>
                  <p className="font-medium">{selectedPet.color}</p>
                </div>
              </div>

              <div className="pt-4 border-t border-[#E8E2D4]">
                <h4 className="font-medium text-[#2B2B2B] mb-2">Sahip Bilgileri</h4>
                {selectedPet.owner ? (
                  <div className="p-3 bg-[#F7F4ED] rounded-lg">
                    <p className="font-medium text-[#2B2B2B]">{selectedPet.owner.name}</p>
                    <p className="text-sm text-[#8A8676]">{selectedPet.owner.phone}</p>
                    <p className="text-sm text-[#8A8676]">{selectedPet.owner.email}</p>
                  </div>
                ) : (
                  <p className="text-sm text-[#8A8676]">Sahip bilgisi bulunamadı</p>
                )}
              </div>

              <div className="pt-4 border-t border-[#E8E2D4]">
                <h4 className="font-medium text-[#2B2B2B] mb-3 flex items-center">
                  <Syringe className="w-4 h-4 mr-2" />
                  Aşı Geçmişi
                </h4>
                
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {filteredVaccinations
                    .filter(vacc => vacc.pet?.id === selectedPet.id)
                    .map((vaccination) => (
                      <div key={vaccination.id} className="p-3 bg-[#F7F4ED] rounded-lg border-l-4 border-blue-500">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium text-[#2B2B2B]">{vaccination.name}</h5>
                            <p className="text-sm text-[#8A8676]">
                              Tarih: {formatDate(vaccination.date)}
                            </p>
                            {vaccination.next_due_date && (
                              <p className="text-sm text-[#8A8676]">
                                Sonraki Aşı: {formatDate(vaccination.next_due_date)}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(vaccination)}`}>
                            {getStatusLabel(vaccination)}
                          </span>
                        </div>
                        {vaccination.notes && (
                          <p className="text-sm text-[#8A8676] mt-2">{vaccination.notes}</p>
                        )}
                      </div>
                    ))}
                </div>


                
                
                {filteredVaccinations.filter(vacc => vacc.pet?.id === selectedPet.id).length === 0 && (
                  <div className="text-center py-4">
                    <Syringe className="w-12 h-12 text-[#B0A99A] mx-auto mb-2" />
                    <p className="text-sm text-[#8A8676]">Bu hastanın henüz aşı kaydı bulunmuyor</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <DeleteConfirmModal
        isOpen={!!vaccinationToDelete}
        onClose={() => setVaccinationToDelete(null)}
        onConfirm={() => vaccinationToDelete ? handleDeleteVaccination(vaccinationToDelete.id) : undefined}
        title="Aşı Kaydını Sil"
        message="Bu aşı kaydı kalıcı olarak silinecek. Bu işlem geri alınamaz."
        itemName={vaccinationToDelete
          ? `${vaccinationToDelete.vaccine_name || vaccinationToDelete.name || 'Aşı'} · ${vaccinationToDelete.date}`
          : undefined}
      />
    </div>
  );
};

export default Vaccinations; 